import React, { useState, useEffect } from 'react';
import { Send, ArrowRight, Lock, Check, AlertCircle, Loader2, Sparkles, CheckCircle2, ArrowLeft, Home, MapPin, Calendar, Users, ShieldCheck, DollarSign, Clock, Building, UserCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useEligibilityLimit } from '../../hooks/useEligibilityLimit';
import PhoneInputNative from '../PhoneInputNative';
import {
    ACC_COUNTRIES,
    ACC_CITIES,
    URGENCY_LEVELS,
    STAY_DURATIONS,
    ACCOMMODATION_TYPES,
    STUDENT_STATUS,
    MUST_HAVE_FILTERS,
    GUARANTOR_STATUS
} from '../../data/AccommodationConstants';

type Step = 'input' | 'thinking' | 'result';

interface PropertyResult {
    id: string;
    title: string;
    location: string;
    price: string;
    type: string;
    distance: string;
    commuteTime: number;
    urgencyScore: number;
    isStudentFriendly: boolean;
    tags: string[];
    description: string;
    image: string;
    sourceLink?: string;
}

interface MatchResponse {
    topMatches: PropertyResult[];
    additionalMatches: Partial<PropertyResult>[];
}

const AccommodationForm = () => {
    const navigate = useNavigate();
    const { canAccess, loading, markAsUsed } = useEligibilityLimit('accommodation');

    // UI State
    const [step, setStep] = useState<Step>('input');
    const [currentSubStep, setCurrentSubStep] = useState(1);
    const [thinkingProgress, setThinkingProgress] = useState(0);
    const [submitted, setSubmitted] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);

    // Form State
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [countryCode, setCountryCode] = useState('+44');
    const [selectedCountry, setSelectedCountry] = useState('GB');

    // Step 1: Destination
    const [destination, setDestination] = useState('');
    const [city, setCity] = useState('');
    const [university, setUniversity] = useState('');

    // Step 2: Timing
    const [urgency, setUrgency] = useState('');
    const [duration, setDuration] = useState('');

    // Step 3: Preferences
    const [accType, setAccType] = useState('');
    const [studentStatus, setStudentStatus] = useState('');
    const [filters, setFilters] = useState<string[]>([]);

    // Step 4: Budget
    const [budget, setBudget] = useState(1000);
    const [guarantor, setGuarantor] = useState('');

    const [result, setResult] = useState<MatchResponse | null>(null);

    const handleFilterToggle = (filter: string) => {
        setFilters(prev => prev.includes(filter) ? prev.filter(f => f !== filter) : [...prev, filter]);
    };

    const handleNext = () => {
        if (currentSubStep < 4) setCurrentSubStep(prev => prev + 1);
        else handleStartMatching();
    };

    const handleBack = () => {
        if (currentSubStep > 1) setCurrentSubStep(prev => prev - 1);
    };

    const handleStartMatching = async () => {
        setStep('thinking');
        setThinkingProgress(0);

        // Simulation of AI processing
        const intervals = [20, 45, 75, 100];
        intervals.forEach((prog, i) => {
            setTimeout(() => setThinkingProgress(prog), (i + 1) * 800);
        });

        try {
            const response = await fetch('/api/predict-accommodation', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    destination, city, university, urgency, duration, accType, studentStatus, filters, budget, guarantor
                })
            });

            if (response.ok) {
                const data = await response.json();
                setResult(data);
                setTimeout(() => setStep('result'), 3200);
            }
        } catch (error) {
            console.error("Matching failed", error);
            setStep('input');
        }
    };

    const handleFinalSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitLoading(true);

        const formData = new FormData();
        formData.append('service', 'accommodation');
        formData.append('name', name);
        formData.append('email', email);
        formData.append('phone', countryCode + phone);
        formData.append('destination', destination);
        formData.append('city', city);
        formData.append('university', university);
        formData.append('urgency', urgency);
        formData.append('duration', duration);
        formData.append('accType', accType);
        formData.append('studentStatus', studentStatus);
        formData.append('filters', filters.join(', '));
        formData.append('budget', budget.toString());
        formData.append('guarantor', guarantor);

        try {
            const response = await fetch('/api/eligibility', {
                method: 'POST',
                body: formData
            });

            if (response.ok) {
                markAsUsed();
                setSubmitted(true);
            }
        } catch (error) {
            console.error("Submission failed", error);
        } finally {
            setSubmitLoading(false);
        }
    };

    if (loading) return <div className="p-8 text-center text-slate-400">Loading Access Rights...</div>;

    if (!canAccess) {
        return (
            <div className="flex flex-col items-center justify-center text-center p-8 space-y-6">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center">
                    <Lock size={32} className="text-gray-400" />
                </div>
                <div>
                    <h3 className="text-xl font-bold text-[#011E26]">Free Eligibility Check Limit Reached</h3>
                    <p className="text-slate-500 mt-2 max-w-md mx-auto">
                        You have already used your free check. Please sign in or create an account to access unlimited AI predictions and detailed accommodation roadmaps.
                    </p>
                </div>
                <div className="flex gap-4">
                    <button onClick={() => navigate('/login')} className="px-6 py-3 bg-white border border-gray-200 text-[#011E26] font-bold rounded hover:border-[#009B8F] transition-all">Sign In</button>
                    <button onClick={() => navigate('/signup')} className="px-6 py-3 bg-[#009B8F] text-white font-bold rounded hover:bg-[#00857a] transition-all">Create Free Account</button>
                </div>
            </div>
        );
    }

    if (step === 'thinking') {
        const thinkingSteps = [
            "Scanning live property portals...",
            "Calculating proximity to " + university + "...",
            "Applying student-friendly ranking...",
            "Personalising your results..."
        ];
        const activeStepIdx = Math.min(Math.floor(thinkingProgress / 26), 3);

        return (
            <div className="py-20 flex flex-col items-center justify-center text-center animate-fadeIn">
                <div className="relative mb-10">
                    <div className="w-32 h-32 border-4 border-[#009B8F]/20 rounded-full animate-spin border-t-[#009B8F]"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                        <Sparkles className="w-12 h-12 text-[#009B8F] animate-pulse" />
                    </div>
                </div>
                <h3 className="text-3xl font-serif text-[#011E26] mb-6">AI Matcher in Progress</h3>
                <div className="w-full max-w-md bg-gray-100 h-2 rounded-full overflow-hidden mb-8">
                    <div className="bg-[#009B8F] h-full transition-all duration-500" style={{ width: `${thinkingProgress}%` }}></div>
                </div>
                <div className="space-y-4">
                    {thinkingSteps.map((s, i) => (
                        <div key={i} className={`flex items-center gap-3 justify-center transition-all ${i <= activeStepIdx ? 'text-[#009B8F] opacity-100' : 'text-slate-300 opacity-50'}`}>
                            {i < activeStepIdx ? <CheckCircle2 size={18} /> : (i === activeStepIdx ? <Loader2 size={18} className="animate-spin" /> : <div className="w-[18px]" />)}
                            <span className="font-bold tracking-wide uppercase text-xs">{s}</span>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (step === 'result' && result) {
        if (submitted) {
            return (
                <div className="text-center py-12 animate-fadeIn bg-white rounded-xl shadow-sm border border-gray-100 p-8">
                    <div className="w-20 h-20 bg-[#009B8F]/10 text-[#009B8F] rounded-full flex items-center justify-center mx-auto mb-8">
                        <UserCheck size={40} />
                    </div>
                    <h3 className="text-3xl font-serif text-[#011E26] mb-4">Application Strategy Received</h3>
                    <p className="text-slate-600 max-w-lg mx-auto text-lg mb-8 leading-relaxed">
                        A NexOverseas Housing Expert will now begin negotiating with these providers on your behalf. We will contact you at <strong>{email}</strong> shortly.
                    </p>
                    <button onClick={() => window.location.reload()} className="px-8 py-3 border-2 border-[#009B8F] text-[#009B8F] font-bold uppercase tracking-widest text-xs hover:bg-[#009B8F] hover:text-white transition-all">
                        Run New Search
                    </button>
                </div>
            );
        }

        return (
            <div className="animate-fadeIn space-y-12">
                <div className="bg-[#011E26] p-8 -mx-8 -mt-8 text-white relative overflow-hidden">
                    <div className="relative z-10">
                        <h3 className="text-3xl font-serif mb-2">Your AI Property Matches</h3>
                        <p className="text-teal-200">20 properties found near {university}. Showing top 3 personalized matches.</p>
                    </div>
                    <Sparkles className="absolute -right-4 -top-4 w-32 h-32 text-white/5" />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {result.topMatches.map((property, idx) => (
                        <div key={idx} className="bg-white border border-gray-100 rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all group">
                            <div className="relative h-48">
                                <img src={property.image} alt={property.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                <div className="absolute top-4 left-4 bg-[#009B8F] text-white text-[10px] font-bold px-3 py-1 rounded uppercase tracking-widest shadow-lg">
                                    Score: {property.urgencyScore}%
                                </div>
                            </div>
                            <div className="p-6">
                                <h4 className="font-serif text-xl mb-1 text-[#011E26]">{property.title}</h4>
                                <p className="text-slate-400 text-sm flex items-center gap-1 mb-4">
                                    <MapPin size={14} /> {property.location}
                                </p>

                                <div className="space-y-3 mb-6">
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="text-slate-500 uppercase font-bold tracking-wider flex items-center gap-2">
                                            <Clock size={14} /> Commute
                                        </span>
                                        <span className="text-[#011E26] font-bold">{property.commuteTime} mins</span>
                                    </div>
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="text-slate-500 uppercase font-bold tracking-wider flex items-center gap-2">
                                            <Building size={14} /> Type
                                        </span>
                                        <span className="text-[#011E26] font-bold">{property.type}</span>
                                    </div>
                                    <div className="flex items-center justify-between text-sm pt-2 border-t border-gray-50">
                                        <span className="text-[#009B8F] font-bold uppercase tracking-widest text-[10px]">Price</span>
                                        <span className="text-[#011E26] font-bold text-lg">{property.price}</span>
                                    </div>
                                </div>

                                <div className="flex flex-wrap gap-2 mb-6">
                                    {property.tags.map(tag => (
                                        <span key={tag} className="bg-gray-50 text-slate-500 text-[9px] font-bold px-2 py-1 rounded uppercase border border-gray-100">
                                            {tag}
                                        </span>
                                    ))}
                                </div>

                                {property.sourceLink ? (
                                    <a href={property.sourceLink} target="_blank" rel="noopener noreferrer" className="w-full py-3 bg-gray-50 hover:bg-white hover:border-[#009B8F] border border-gray-100 text-slate-600 font-bold uppercase tracking-widest text-[10px] transition-all flex items-center justify-center gap-2 text-center">
                                        View Details <ArrowRight size={14} />
                                    </a>
                                ) : (
                                    <button className="w-full py-3 bg-gray-50 hover:bg-white hover:border-[#009B8F] border border-gray-100 text-slate-600 font-bold uppercase tracking-widest text-[10px] transition-all flex items-center justify-center gap-2">
                                        View Details <ArrowRight size={14} />
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="space-y-4">
                    <h4 className="text-lg font-bold text-[#011E26] uppercase tracking-widest">17 Additional Matches Nearby</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {[1, 2, 3, 4].map(i => (
                            <div key={i} className="bg-white border border-gray-100 rounded-lg p-4 blur-[3px] pointer-events-none opacity-50 relative">
                                <div className="w-full h-12 bg-gray-200 rounded mb-4"></div>
                                <div className="h-4 bg-gray-100 w-3/4 mb-2"></div>
                                <div className="h-4 bg-gray-100 w-1/2"></div>
                            </div>
                        ))}
                    </div>
                    <div className="bg-[#009B8F]/5 border border-dashed border-[#009B8F] p-8 rounded-xl text-center">
                        <Lock className="w-12 h-12 text-[#009B8F] mx-auto mb-4" />
                        <h4 className="text-xl font-serif text-[#011E26] mb-2">Unlock 17 More Verified Listings</h4>
                        <p className="text-slate-600 mb-6 max-w-md mx-auto">Get full access to all verified listings, student reviews, and direct chat with landlords.</p>
                        <div className="flex justify-center gap-4">
                            <button onClick={() => navigate('/login')} className="px-6 py-3 bg-white border border-gray-200 text-[#011E26] font-bold uppercase tracking-widest text-xs hover:border-[#009B8F] transition-all">Sign In</button>
                            <button onClick={() => navigate('/signup')} className="px-6 py-3 bg-[#009B8F] text-white font-bold uppercase tracking-widest text-xs hover:bg-[#00857a] transition-all shadow-lg">Login to Apply via Nex-AI</button>
                        </div>
                    </div>
                </div>

                <div className="bg-gray-50 border border-gray-100 rounded-xl p-8">
                    <h4 className="text-xl font-serif text-[#011E26] mb-6 flex items-center gap-2">
                        <ShieldCheck className="text-[#009B8F]" /> Secure Your Housing Strategy
                    </h4>
                    <form onSubmit={handleFinalSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
                        <div className="space-y-1">
                            <label className="text-[10px] font-bold text-[#011E26] uppercase tracking-wider">Full Name</label>
                            <input required value={name} onChange={e => setName(e.target.value)} type="text" className="w-full p-3 bg-white border border-gray-200 outline-none" placeholder="John Doe" />
                        </div>
                        <div className="space-y-1">
                            <label className="text-[10px] font-bold text-[#011E26] uppercase tracking-wider">Email Address</label>
                            <input required value={email} onChange={e => setEmail(e.target.value)} type="email" className="w-full p-3 bg-white border border-gray-200 outline-none" placeholder="john@example.com" />
                        </div>
                        <div className="space-y-1">
                            <PhoneInputNative
                                phone={phone}
                                countryCode={countryCode}
                                onPhoneChange={setPhone}
                                onCountryCodeChange={(code, country) => { setCountryCode(code); setSelectedCountry(country); }}
                            />
                        </div>
                        <div className="md:col-span-3">
                            <button disabled={submitLoading} type="submit" className="w-full py-4 bg-[#011E26] text-white font-bold uppercase tracking-widest text-xs hover:bg-[#00151a] transition-all flex items-center justify-center gap-3">
                                {submitLoading ? <Loader2 className="animate-spin" /> : <><Sparkles size={18} className="text-[#009B8F]" /> Apply via Nex-AI Agent</>}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <div className="animate-fadeIn">
            {/* Progress Header */}
            <div className="flex items-center justify-between mb-10 overflow-x-auto pb-4">
                {[
                    { id: 1, label: 'Destination', icon: MapPin },
                    { id: 2, label: 'Timing', icon: Clock },
                    { id: 3, label: 'Preferences', icon: Home },
                    { id: 4, label: 'Budget', icon: DollarSign }
                ].map((s) => (
                    <div key={s.id} className="flex items-center min-w-max">
                        <div className={`flex items-center gap-3 px-4 py-2 rounded-full transition-all ${currentSubStep === s.id ? 'bg-[#009B8F] text-white shadow-lg' : currentSubStep > s.id ? 'text-[#009B8F]' : 'text-slate-300'}`}>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${currentSubStep === s.id ? 'border-white' : currentSubStep > s.id ? 'border-[#009B8F] bg-[#009B8F]/10' : 'border-slate-200'}`}>
                                {currentSubStep > s.id ? <Check size={16} /> : <s.icon size={16} />}
                            </div>
                            <span className="text-[10px] font-bold uppercase tracking-widest">{s.label}</span>
                        </div>
                        {s.id < 4 && <div className={`w-8 h-px mx-4 ${currentSubStep > s.id ? 'bg-[#009B8F]' : 'bg-slate-200'}`} />}
                    </div>
                ))}
            </div>

            <div className="min-h-[400px]">
                {/* Step 1: Destination & University */}
                {currentSubStep === 1 && (
                    <div className="animate-fadeIn space-y-8">
                        <div>
                            <h4 className="text-2xl font-serif text-[#011E26] mb-2">Step 1 — Choose Your Destination</h4>
                            <p className="text-slate-500">Select where you plan to study and your institution.</p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="space-y-4">
                                <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider block">Target Country</label>
                                <div className="grid grid-cols-2 gap-3">
                                    {ACC_COUNTRIES.map(c => (
                                        <button key={c} onClick={() => { setDestination(c); setCity(''); }} className={`p-4 text-xs font-bold border rounded-sm transition-all ${destination === c ? 'bg-[#009B8F] text-white border-[#009B8F] shadow-md' : 'bg-gray-50 text-slate-600 border-gray-200 hover:border-[#009B8F]'}`}>
                                            {c}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="space-y-6">
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider">City / Area</label>
                                    <select value={city} onChange={e => setCity(e.target.value)} className="w-full p-4 bg-gray-50 border border-gray-200 focus:border-[#009B8F] outline-none rounded-sm">
                                        <option value="">Select City</option>
                                        {destination && ACC_CITIES[destination]?.map(c => (
                                            <option key={c} value={c}>{c}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider">Your University</label>
                                    <div className="relative">
                                        <input value={university} onChange={e => setUniversity(e.target.value)} type="text" className="w-full p-4 pl-12 bg-gray-50 border border-gray-200 focus:border-[#009B8F] outline-none rounded-sm" placeholder="e.g. University College London" />
                                        <Building className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={20} />
                                    </div>
                                    <p className="text-[10px] text-slate-400 italic">Used to calculate your precise commute time.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Step 2: Move-In Timing */}
                {currentSubStep === 2 && (
                    <div className="animate-fadeIn space-y-8">
                        <div>
                            <h4 className="text-2xl font-serif text-[#011E26] mb-2">Step 2 — Move-In Timing</h4>
                            <p className="text-slate-500">Tell us how urgent your accommodation search is.</p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="space-y-4">
                                <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider block">Move-In Urgency</label>
                                <div className="space-y-3">
                                    {URGENCY_LEVELS.map(u => (
                                        <button key={u.id} onClick={() => setUrgency(u.id)} className={`w-full p-4 text-left text-sm font-bold border flex items-center justify-between transition-all ${urgency === u.id ? 'bg-[#009B8F] text-white border-[#009B8F] shadow-md' : 'bg-gray-50 text-slate-600 border-gray-200 hover:border-[#009B8F]'}`}>
                                            {u.label}
                                            {urgency === u.id && <Check size={18} />}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="space-y-4">
                                <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider block">Stay Duration</label>
                                <div className="space-y-3">
                                    {STAY_DURATIONS.map(d => (
                                        <button key={d.id} onClick={() => setDuration(d.id)} className={`w-full p-4 text-left text-sm font-bold border flex items-center justify-between transition-all ${duration === d.id ? 'bg-[#009B8F] text-white border-[#009B8F] shadow-md' : 'bg-gray-50 text-slate-600 border-gray-200 hover:border-[#009B8F]'}`}>
                                            {d.label}
                                            {duration === d.id && <Check size={18} />}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Step 3: Living Preferences */}
                {currentSubStep === 3 && (
                    <div className="animate-fadeIn space-y-8">
                        <div>
                            <h4 className="text-2xl font-serif text-[#011E26] mb-2">Step 3 — Living Preferences</h4>
                            <p className="text-slate-500">Choose the type of accommodation and your requirements.</p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            <div className="space-y-4">
                                <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider block text-[#009B8F]">Accommodation Type</label>
                                <div className="space-y-2">
                                    {ACCOMMODATION_TYPES.map(t => (
                                        <button key={t.id} onClick={() => setAccType(t.id)} className={`w-full p-3 text-left text-xs font-bold border rounded-sm transition-all ${accType === t.id ? 'bg-[#011E26] text-white border-[#011E26]' : 'bg-gray-50 text-slate-600 border-gray-200 hover:border-[#011E26]'}`}>
                                            {t.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="space-y-4">
                                <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider block text-[#009B8F]">Student Status</label>
                                <div className="space-y-2">
                                    {STUDENT_STATUS.map(s => (
                                        <button key={s.id} onClick={() => setStudentStatus(s.id)} className={`w-full p-3 text-left text-xs font-bold border rounded-sm transition-all ${studentStatus === s.id ? 'bg-[#011E26] text-white border-[#011E26]' : 'bg-gray-50 text-slate-600 border-gray-200 hover:border-[#011E26]'}`}>
                                            {s.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="space-y-4">
                                <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider block text-[#009B8F]">Must-Have Filters</label>
                                <div className="space-y-2">
                                    {MUST_HAVE_FILTERS.map(f => (
                                        <label key={f.id} className={`flex items-center gap-3 p-3 border rounded-sm cursor-pointer transition-all ${filters.includes(f.id) ? 'bg-[#011E26] text-white border-[#011E26]' : 'bg-gray-50 text-slate-600 border-gray-200 hover:border-[#011E26]'}`}>
                                            <input type="checkbox" checked={filters.includes(f.id)} onChange={() => handleFilterToggle(f.id)} className="hidden" />
                                            <div className={`w-4 h-4 rounded-sm border flex items-center justify-center ${filters.includes(f.id) ? 'bg-[#009B8F] border-[#009B8F]' : 'border-slate-300 bg-white'}`}>
                                                {filters.includes(f.id) && <Check size={12} className="text-white" />}
                                            </div>
                                            <span className="text-xs font-bold">{f.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Step 4: Budget & Guarantor */}
                {currentSubStep === 4 && (
                    <div className="animate-fadeIn space-y-12">
                        <div>
                            <h4 className="text-2xl font-serif text-[#011E26] mb-2">Step 4 — Budget & Guarantor</h4>
                            <p className="text-slate-500">Finalize your financial preferences.</p>
                        </div>
                        <div className="max-w-2xl mx-auto space-y-12">
                            <div className="space-y-6">
                                <div className="flex justify-between items-center mb-4">
                                    <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider">Monthly Budget (GBP)</label>
                                    <span className="text-3xl font-serif text-[#009B8F]">£{budget}</span>
                                </div>
                                <input type="range" min="400" max="3000" step="50" value={budget} onChange={e => setBudget(parseInt(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#009B8F]" />
                                <div className="flex justify-between text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                    <span>£400</span>
                                    <span>£3,000+</span>
                                </div>
                            </div>
                            <div className="space-y-4">
                                <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider block">Guarantor Status</label>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {GUARANTOR_STATUS.map(g => (
                                        <button key={g.id} onClick={() => setGuarantor(g.id)} className={`p-4 text-center text-xs font-bold border rounded-sm transition-all ${guarantor === g.id ? 'bg-[#009B8F] text-white border-[#009B8F] shadow-lg' : 'bg-gray-50 text-slate-600 border-gray-200 hover:border-[#009B8F]'}`}>
                                            {g.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Navigation Buttons */}
            <div className="mt-12 pt-8 border-t border-gray-100 flex justify-between items-center">
                <button onClick={handleBack} disabled={currentSubStep === 1} className={`flex items-center gap-2 px-6 py-2 text-xs font-bold uppercase tracking-widest transition-all ${currentSubStep === 1 ? 'text-slate-300 cursor-not-allowed' : 'text-[#011E26] hover:text-[#009B8F]'}`}>
                    <ArrowLeft size={16} /> Back
                </button>
                <button onClick={handleNext} className="bg-[#011E26] text-white px-10 py-4 font-bold uppercase tracking-widest text-xs hover:bg-[#00151a] transition-all flex items-center gap-3 shadow-lg group">
                    {currentSubStep === 4 ? (
                        <>Run AI Matcher <Sparkles size={16} className="text-[#009B8F] group-hover:animate-spin" /></>
                    ) : (
                        <>Continue <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" /></>
                    )}
                </button>
            </div>
        </div>
    );
};

export default AccommodationForm;
